import { createContext, useContext } from "react";
import invariant from "tiny-invariant";
import { z } from "zod";

export const lstring = z.strictObject({ en: z.string(), ru: z.string() });
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type lstring = z.infer<typeof lstring>;

export const Lang = z.enum(["en", "ru"]);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type Lang = z.infer<typeof Lang>;

export type Lstr = {
  en: string | { one: string; few: string; };
  ru: string | { one: string; two: string; few: string; };
};

export const GameVer = z.enum(["c1", "c2", "c3", "c4", "c5", "il", "gf"]);
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type GameVer = z.infer<typeof GameVer>;

export type Realm = {
  lang: Lang;
  game: GameVer;
};

export const GAME_VER_ORD: Record<GameVer, number> = {
  c1: 1,
  c2: 2,
  c3: 3,
  c4: 4,
  c5: 5,
  il: 6,
  gf: 7,
};

export function tt(lang: Lang, lstr: Lstr, c?: number): string {
  switch (lang) {
    case "en": {
      const en = lstr.en;
      if (typeof en === "string") {
        return en;
      } else if (c === 1) {
        return en.one;
      } else {
        return en.few;
      }
    }
    case "ru": {
      const ru = lstr.ru;
      const n = c ?? 0;
      if (typeof ru === "string") {
        return ru;
      } else if (n % 10 === 1 && n % 100 !== 11) {
        return ru.one;
      } else if (
        [2, 3, 4].includes(n % 10) &&
        ![12, 13, 14].includes(n % 100)
      ) {
        return ru.two;
      } else {
        return ru.few;
      }
    }
  }
}

export const DEFAULT_REALM: Realm = createRealm({ lang: "en", game: "gf" });
const REALM_PATH_MAP: Record<string, Realm | undefined> = {
  c1: { game: "c1", lang: "en" },
  "c1-ru": { game: "c1", lang: "ru" },
  c2: { game: "c2", lang: "en" },
  "c2-ru": { game: "c2", lang: "ru" },
  c3: { game: "c3", lang: "en" },
  "c3-ru": { game: "c3", lang: "ru" },
  c4: { game: "c4", lang: "en" },
  "c4-ru": { game: "c4", lang: "ru" },
  c5: { game: "c5", lang: "en" },
  "c5-ru": { game: "c5", lang: "ru" },
  il: { game: "il", lang: "en" },
  "il-ru": { game: "il", lang: "ru" },
  ru: { game: "gf", lang: "ru" },
};

export function createRealm(opts: { lang: Lang; game: GameVer; }): Realm {
  return opts;
}

export const RealmContext = createContext<Realm>(DEFAULT_REALM);

export function useRealm() {
  return useContext(RealmContext);
}

export function parseRealm(ver: string): null | Realm {
  return REALM_PATH_MAP[ver] ?? null;
}

export function realmFromPath(path: string) {
  if (path.startsWith("/")) {
    const nextSlash = path.indexOf("/", 1);
    if (nextSlash !== -1) {
      const part = path.slice(1, nextSlash);
      const parsed = parseRealm(part);
      if (parsed) {
        return parsed;
      }
    }
  }
  return DEFAULT_REALM;
}

export function stripRealm(path: string) {
  if (path.startsWith("/")) {
    const nextSlash = path.indexOf("/", 1);
    const part = nextSlash === -1 ? path.slice(1) : path.slice(1, nextSlash);
    if (REALM_PATH_MAP[part]) {
      return nextSlash === -1 ? "/" : path.slice(nextSlash);
    }
  }
  return path;
}

export function useT() {
  const realm = useContext(RealmContext);
  return tt.bind(null, realm.lang);
}

export function tturl(realm: Realm, url: string) {
  invariant(url.startsWith("/"));
  const parts = [];
  if (realm.game !== DEFAULT_REALM.game) {
    parts.push(realm.game);
  }
  if (realm.lang !== DEFAULT_REALM.lang) {
    parts.push(realm.lang);
  }
  const prefix = parts.length > 0 ? "/" + parts.join("-") : "";
  return prefix + url;
}

export function useTurl() {
  const realm = useContext(RealmContext);
  return tturl.bind(null, realm);
}
